<template>
  <div id="root">
    <div class="alert alert-error w-1/2 mx-auto mt-3" v-if="error">
      <AlertCircle />
      <span>{{ error }}</span>
    </div>

    <div class="page-header inner">
      <h1 data-test="title">Management Reporting Dashboard</h1>

      <ManagedDateRange :initialStartDate="startDate"
                        :initialEndDate="endDate"
                        :onApply="setDate" />
    </div>

    <FilterBar>
      <GenericMultiFilter
        v-model="selectedCoverageTypes"
        :options="coverageTypes"
        title="Coverage Type"
        data-test="coverage-type-filter" />
      <GenericMultiFilter
        v-model="selectedSicIndustries"
        :options="sicIndustries"
        title="SIC Industry"
        chip-title-field="label"
        data-test="sic-industry-filter" />
      <GenericMultiFilter
        v-model="selectedIndustries"
        :options="industries"
        title="Industry"
        chip-title-field="label"
        data-test="industry-filter">
        <template #menu-actions>
          <Toggle
            v-model="includeChildren"
            label="Include Children"
            variant="primary"
            size="sm" />
        </template>
      </GenericMultiFilter>
      <GenericMultiFilter
        v-model="selectedDepartments"
        :options="departments"
        title="Department"
        data-test="department-filter" />
      <GenericMultiFilter
        v-model="selectedAgencyTypes"
        :options="agencyTypes"
        title="CAC Entity"
        data-test="agency-type-filter" />
      <GenericMultiFilter
        v-model="selectedProfitCenters"
        :options="profitCenters"
        title="Profit Center"
        chip-title-field="label"
        data-test="profit-center-filter" />
      <div class="flex items-center pt-0 ml-4">
        <AppButton
          v-if="filterApplied"
          outline
          @click="clearAllFilters()">
          Reset Filters
        </AppButton>
      </div>
    </FilterBar>

    <Statistics :statistics="statisticsTransformed"/>

    <main class="auto-grid graphs">
      <div v-if="!loading && !error" class="col-span-3 m-8" data-test="premium-by-month-chart">
        <fusioncharts type="mscolumn2d" width="100%" height="500" dataFormat="json"
                      :dataSource="premiumByMonthDataSource" />
      </div>

      <div v-if="!loading && !error" class="col-span-3 m-8" data-test="premium-by-billmode-and-wholesale-charts">
        <div class="doughnuts">
          <fusioncharts type="doughnut2d" width="100%" height="500" dataFormat="json"
                        :dataSource="premiumByBillmodeDataSource" />
          <fusioncharts type="doughnut2d" width="100%" height="500" dataFormat="json"
                        :dataSource="premiumByWholesaleVsRetailDataSource" />
        </div>
      </div>

      <div v-if="!loading && !error" class="col-span-3 m-8" data-test="premium-by-carrier-chart">
        <fusioncharts type="stackedcolumn2d" width="100%" height="500" dataFormat="json"
                      :dataSource="premiumByCarrierDataSource" />
      </div>

      <div v-if="!loading && !error" class="col-span-3 m-8" data-test="premium-by-department-chart">
        <fusioncharts type="bar2d" width="100%" height="500" dataFormat="json"
                      :dataSource="premiumByDepartmentDataSource" />
      </div>

      <div v-if="!loading && !error" class="col-span-3 m-8" data-test="premium-by-industry-chart">
        <fusioncharts type="stackedbar2d" width="100%" height="500" dataFormat="json"
                      :dataSource="premiumByIndustryDataSource" />
      </div>

      <div v-if="!loading && !error" class="col-span-3 m-8" data-testid="premium-by-product">
        <fusioncharts type="msline" width="100%" height="500" dataFormat="json"
          :dataSource="premiumByProductDataSource"
        />
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import ManagedDateRange from '@/components/ManagedDateRange.vue';
import { FinancialStatistics } from '@/types/FinancialStatistics';
import {
  managementAnalyticsService,
  PremiumByBillmodeData,
  PremiumByWholesaleVsRetailData
} from '@/services/ManagementAnalyticsApi';
import { FailedToLoadPage } from '@/utilities/Errors';
import { numToMoneyString } from '@/utilities/Money';
import { DateTime } from 'luxon';
import { DateChangePayload } from '@/types/components/DateChangePayload';
import { LabelValue, LabelValueYearT12 } from '@/types/label-value';
import Statistics from '@/components/Statistics.vue';
import GenericMultiFilter from '@/components/v2/GenericMultiFilter.vue';
import { referenceService } from '@/services/api';
import FilterBar from '@/components/v2/FilterBar.vue';
import Toggle from '@/components/v2/Toggle.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { chartMutedColor, defaultChartColors, stackedSeriesColors } from '@/utilities/Color';
import { MultiSeries } from '@/types/MultiSeries';
import { AlertCircle } from '@lucide/vue';
import { DoughnutData, FusionChartDoughnut2D } from "@/types/Doughnut";
import { paletteColors } from '@/utilities/Color';
import { compactNumber } from "@/utilities/Number";

const premiumByMonthChart = {
  animation: '1',
  caption: 'Premium Total by Month',
  xAxisName: 'Year and Month',
  yAxisName: 'Premium',
  placeValuesInside: false,
  theme: 'cacs',
  exportFilename: 'CACS Total Premium By Month',
  plotSpacePercent: 10,
  valuePadding: -2,
  showAlternateHGridColor: false,
  paletteColors: defaultChartColors,
  forceYAxisValueDecimals: false,
};

const doughnutChart = {
  animation: '1',
  forceYAxisValueDecimals: false,
  decimals: 0,
  showLegend:true,
  theme: 'cacs',
  paletteColors: paletteColors,
  showBorder: false,
  doughnutRadius: '55%'
};

const premiumByCarrierChart = {
  animation: '1',
  caption: 'Total Premium by Carrier',
  xAxisname: 'Carrier',
  yAxisName: 'Premium',
  theme: 'cacs',
  exportFilename: 'CACS Total Premium By Carrier',
  showSum: true,
  showValues: false,
  paletteColors: stackedSeriesColors,
  forceYAxisValueDecimals: false,
  decimals: 1,
};

const premiumByDepartmentChart = {
  animation: '1',
  caption: 'Premium Total by Department',
  xAxisName: 'Department',
  yAxisName: 'Premium',
  placeValuesInside: false,
  theme: 'cacs',
  exportFilename: 'CACS Total Premium By Department',
  showAlternateHGridColor: false,
  paletteColors: defaultChartColors,
  forceDecimals: false,
};

const premiumByIndustryChart = {
  animation: '1',
  caption: 'Top 10 Premium Total by Industry',
  xAxisName: 'Industry',
  yAxisName: 'Premium',
  plotSpacePercent: 30,
  theme: 'cacs',
  exportFilename: 'CACS Top 10 Total Premium By Industry',
  showSum: true,
  showValues: false,
  paletteColors: stackedSeriesColors,
  forceYAxisValueDecimals: false,
  decimals: 1,
};

const premiumByProductChart = {
  animation: '1',
  caption: 'Premium By Product',
  xAxisName: 'Year',
  yAxisName: 'Premium',
  theme: 'cacs',
  exportFilename: 'Premium By Product',
  paletteColors: stackedSeriesColors,
  valueFontAlpha: 0,
  showValues: false,
  decimals: 1,
  forceYAxisValueDecimals: false,
};

function getNewMaximum<T>(table: Array<T>, getValue: (arg: T) => number): number {
  const highestValue = table.reduce((acc, curr) => {
    const val = getValue(curr);
    return val > acc ? val : acc;
  }, 0);
  return Math.ceil((highestValue + (highestValue * .35)) / 10) * 10;
}

function formatBillmodeDoughnutChartData(data: PremiumByBillmodeData[]): DoughnutData[] {
  return data.map((x => ({
        label: x.billMode === 'A' ? 'Brokerage' : 'Direct',
        value: x.policyPremium,
        displayValue: x.value
      })
  ));
}

function formatWholesaleVsRetailDoughnutChartData(data: PremiumByWholesaleVsRetailData[]): DoughnutData[] {
  return data.map((x => ({
        label: x.wholesale === 0 ? 'Retail' : 'Wholesale',
        value: x.policyPremium,
        displayValue: x.value
      })
  ));
}

export default {
  components: { AlertCircle, Statistics, ManagedDateRange, GenericMultiFilter, FilterBar, Toggle, AppButton },
  data: () => ({
    loading: true,
    loadingStatistics: true,
    error: undefined as undefined | string,
    statistics: {} as Partial<Record<keyof FinancialStatistics, string>>,
    premiumByMonthData: {} as Record<string, unknown>,
    premiumByBillmodeData: [] as Array<PremiumByBillmodeData>,
    premiumByWholesaleVsRetailData: [] as Array<PremiumByWholesaleVsRetailData>,
    premiumByCarrierData: {} as MultiSeries,
    premiumByDepartmentData: [] as Array<LabelValue>,
    premiumByIndustryData: {} as MultiSeries,
    serviceAgreementCompensationData: {} as MultiSeries,
    premiumByProductData: [] as LabelValueYearT12[],
    startDate: DateTime.local().minus({ year: 1 }).startOf('month').toJSDate(),
    endDate: DateTime.local().minus({ month: 1 }).endOf('month').toJSDate(),
    departments: [] as LabelValue<string>[],
    sicIndustries: [] as LabelValue<string>[],
    industries: [] as LabelValue<number>[],
    coverageTypes: [] as LabelValue<string>[],
    agencyTypes: [] as LabelValue<string>[],
    profitCenters: [] as LabelValue[],
    selectedDepartments: [] as string[],
    selectedSicIndustries: [] as string[],
    selectedIndustries: [] as number[],
    includeChildren: false,
    selectedCoverageTypes: [] as string[],
    selectedAgencyTypes: [] as string[],
    selectedProfitCenters: [] as number[],
    showCompare: false,
    agencyColorMap: new Map<string, string>(),
    mappedDepartmentColors: new Map<string, string>(),
  }),
  computed: {
    statisticsTransformed() {
      const isLoading = this.loadingStatistics && !this.error;
      return [
        { key: 'customers', value: this.statistics.customers, heading: 'Clients', loading: isLoading },
        { key: 'premium-total', value: this.statistics.premiumTotal, heading: 'Total Premium', loading: isLoading },
        { key: 'revenue-total', value: this.statistics.revenueTotal, heading: 'Total Revenue Billed', loading: isLoading },
        { key: 'commission-billed', value: this.statistics.commissionBilled, heading: 'Total Commission Billed', loading: isLoading },
        { key: 'marketYield', value: this.statistics.marketYield, heading: 'Market Yield', loading: isLoading, toolTip: 'Market Yield is calculated from billed commission and premium. Fees, audit charges, and policies without commission are excluded.' },
        { key: 'rawYield', value: this.statistics.rawYield, heading: 'Raw Yield', loading: isLoading, toolTip: 'Raw Yield is calculated using total revenue (fees, audit charges, and billed commission) and premium.' },
        { key: 'fees-billed', value: this.statistics.feesBilled, heading: 'Total Client Fees', loading: isLoading },
      ];
    },
    premiumByMonthDataSource() {
      return {
        chart: {
          ...premiumByMonthChart,
        },
        ...this.premiumByMonthData,
      };
    },
    premiumByBillmodeDataSource(): FusionChartDoughnut2D {
      return {
        chart: {
          ...doughnutChart,
          caption: 'Premium Total by Placement',
          exportFilename: 'CACS Premium Total by Placement',
        },
        data: formatBillmodeDoughnutChartData(this.premiumByBillmodeData),
      };
    },
    premiumByWholesaleVsRetailDataSource(): FusionChartDoughnut2D {
      return {
        chart: {
          ...doughnutChart,
          caption: 'Premium Total by Distribution',
          exportFilename: 'CACS Premium Total by Distribution',
        },
        data: formatWholesaleVsRetailDoughnutChartData(this.premiumByWholesaleVsRetailData),
      };
    },
    premiumByProductDataSource() {
      const groupedByProduct = this.premiumByProductData.reduce((acc, d) => {
        if (!acc.has(d.label)) acc.set(d.label, []);
        acc.get(d.label)?.push(d);
        return acc;
      }, new Map<string, LabelValueYearT12[]>());
      
      const products = Array.from(groupedByProduct.keys());
      const allYears = Array.from(new Set(this.premiumByProductData.map(d => d.year))).sort();

      const dataset = products.map(product => {
          const productData = groupedByProduct.get(product);
          const data = allYears.map(year => {
            const yearData = productData?.find(d => d.year === year);
            return { value: yearData ? yearData.value : null };
          });

          return {
            seriesname: product,
            showValues: true,
            color: this.mappedDepartmentColors.get(product),
            data
          };
        }
      );
      
      const categories = allYears.map(year => ({ label: year.toString() }));
      
      return {
        chart: premiumByProductChart,
        categories: [{ category: categories }],
        dataset,
      };
    },
    premiumByCarrierDataSource() {
      return {
        chart: { ...premiumByCarrierChart },
        ...this.premiumByCarrierData,
      };
    },
    premiumByDepartmentDataSource() {
      return {
        chart: premiumByDepartmentChart,
        data: this.premiumByDepartmentData,
      };
    },
    premiumByIndustryDataSource() {
      return {
        chart: { ...premiumByIndustryChart },
        ...this.premiumByIndustryData,
      };
    },
    filterApplied() {
      return this.selectedDepartments.length > 0
          || this.selectedCoverageTypes.length > 0
          || this.selectedSicIndustries.length > 0
          || this.selectedIndustries.length > 0
          || this.selectedAgencyTypes.length > 0
          || this.includeChildren;
    }
  },
  async created() {
    await this.refreshData();
  },
  methods: {
    // map department colors to ensure consistency across all charts where departments are used
    mapDepartmentColors(departments: string[]) {
      const spokenForColors = Array.from(this.mappedDepartmentColors.values());
      let colorsToChooseFrom = defaultChartColors.filter(color => !spokenForColors.includes(color));

      for (const index in departments) {
        if (colorsToChooseFrom.length === 0) {
          // if we run out of colors, reset the colors to choose from
          colorsToChooseFrom = [...defaultChartColors];
        }

        if (this.mappedDepartmentColors.has(departments[index])) continue;

        const color = colorsToChooseFrom.pop();
        if (color) {
          this.mappedDepartmentColors.set(departments[index], color);
        }
      }
    },
    async refreshData() {
      this.loading = true;
      this.error = undefined;

      try {
        await Promise.all([
          this.loadPremiumByMonth(),
          this.loadPremiumByWholesaleVsRetail(),
          this.loadPremiumByDepartment(),
          this.loadPremiumByBillmode(),
          this.loadPremiumByIndustry(),
          this.loadPremiumByProduct(),
          this.loadStatistics(),
          this.loadPremiumByCarrier(),
        ]);

      } catch (e) {
        console.error(e);
        this.error = FailedToLoadPage;
      }

      this.loading = false;
    },
    setDate(datePayload: DateChangePayload) {
      if (datePayload.startDate) {
        this.startDate = datePayload.startDate;
      }
      if (datePayload.endDate) {
        this.endDate = datePayload.endDate;
      }
      this.refreshData();
    },
    async loadStatistics() {
      this.loadingStatistics = true;

      const statistics = await managementAnalyticsService.statistics(
          DateTime.fromJSDate(this.startDate).toISODate(),
          DateTime.fromJSDate(this.endDate).toISODate(),
          this.selectedDepartments,
          this.selectedSicIndustries,
          this.selectedIndustries,
          this.includeChildren,
          this.selectedCoverageTypes,
          this.selectedAgencyTypes,
          this.selectedProfitCenters,
      );
      this.statistics = {
        customers: statistics.customers.toString(),
        premiumBilled: `${numToMoneyString(statistics.premiumBilled / 1000000, 2)}M`,
        premiumEstimated: `${numToMoneyString(statistics.premiumEstimated / 1000000, 2)}M`,
        premiumTotal: `${numToMoneyString(statistics.premiumTotal / 1000000, 2)}M`,
        premiumWholesalerMGA: `${numToMoneyString(statistics.premiumWholesalerMGA / 1000000, 2)}M`,
        feesBilled: `${numToMoneyString(statistics.feesBilled / 1000000, 2)}M`,
        commissionBilled: `${numToMoneyString(statistics.commissionBilled / 1000000, 2)}M`,
        revenueTotal: `${numToMoneyString(statistics.revenueTotal / 1000000, 2)}M`,
        marketYield: `${statistics.marketYield}%`,
        rawYield: `${statistics.rawYield}%`,
      };

      this.loadingStatistics = false;
    },
    async loadPremiumByMonth() {
      const premiumByMonthData = await managementAnalyticsService.premiumByMonth(
          DateTime.fromJSDate(this.startDate).toISODate(),
          DateTime.fromJSDate(this.endDate).toISODate(),
          this.selectedDepartments,
          this.selectedSicIndustries,
          this.selectedIndustries,
          this.includeChildren,
          this.selectedCoverageTypes,
          this.selectedAgencyTypes,
          this.selectedProfitCenters,
      );
      const prevStart = DateTime.fromJSDate(new Date(this.startDate)).minus({ years: 1 }).plus({ days: 1 });
      const prevEnd = DateTime.fromJSDate(new Date(this.endDate)).minus({ years: 1 }).plus({ days: 1 });
      const previousData = await managementAnalyticsService.premiumByMonth(
          prevStart.toISODate(),
          prevEnd.toISODate(),
          this.selectedDepartments,
          this.selectedSicIndustries,
          this.selectedIndustries,
          this.includeChildren,
          this.selectedCoverageTypes,
          this.selectedAgencyTypes,
          this.selectedProfitCenters,
      );
      const max = Math.max(getNewMaximum(premiumByMonthData, x => x.value), getNewMaximum(previousData, x => x.value));
      this.premiumByMonthData = {
        yAxisMaxValue: max,
        categories: [
          {
            category: premiumByMonthData.map(x => ({ label: x.label.split(' ')[0] }))
          }
        ],
        dataset: [
          {
            seriesname: 'Current',
            data: premiumByMonthData.map(x => ({ value: x.value, toolText: x.label }))
          },
          {
            seriesname: 'Previous',
            color: chartMutedColor,
            data: previousData.map(x => ({ value: x.value, toolText: x.label }))
          },
        ]
      };
    },
    async loadPremiumByBillmode() {
      const premByBillmode = await managementAnalyticsService.premiumByBillmode(
          DateTime.fromJSDate(this.startDate).toISODate(),
          DateTime.fromJSDate(this.endDate).toISODate(),
          this.selectedDepartments,
          this.selectedSicIndustries,
          this.selectedIndustries,
          this.includeChildren,
          this.selectedCoverageTypes,
          this.selectedAgencyTypes,
          this.selectedProfitCenters,
      );
      this.premiumByBillmodeData = premByBillmode.map((d) => ({ ...d, value: compactNumber(d.policyPremium)}))
    },
    async loadPremiumByWholesaleVsRetail() {
      const premByWholesaleVsRetail = await managementAnalyticsService.premiumByWholesaleVsRetail(
          DateTime.fromJSDate(this.startDate).toISODate(),
          DateTime.fromJSDate(this.endDate).toISODate(),
          this.selectedDepartments,
          this.selectedSicIndustries,
          this.selectedIndustries,
          this.includeChildren,
          this.selectedCoverageTypes,
          this.selectedAgencyTypes,
          this.selectedProfitCenters,
      );
      this.premiumByWholesaleVsRetailData = premByWholesaleVsRetail.map((d) => ({ ...d, value: compactNumber(d.policyPremium)}))
    },
    async loadPremiumByCarrier() {
      this.premiumByCarrierData = await managementAnalyticsService.premiumByCarrier(
          DateTime.fromJSDate(this.startDate).toISODate(),
          DateTime.fromJSDate(this.endDate).toISODate(),
          10,
          this.selectedDepartments,
          this.selectedSicIndustries,
          this.selectedIndustries,
          this.includeChildren,
          this.selectedCoverageTypes,
          this.selectedAgencyTypes,
          this.selectedProfitCenters,
      );
    },
    async loadPremiumByDepartment() {
      const premiumByDept = await managementAnalyticsService.premiumByDepartment(
          DateTime.fromJSDate(this.startDate).toISODate(),
          DateTime.fromJSDate(this.endDate).toISODate(),
          this.selectedDepartments,
          this.selectedSicIndustries,
          this.selectedIndustries,
          this.includeChildren,
          this.selectedCoverageTypes,
          this.selectedAgencyTypes,
          this.selectedProfitCenters,
      );

      premiumByDept.forEach(p => p.label = p.label ?? 'Unknown');

      // assign colors to departments
      const departments = [...new Set(premiumByDept.map((d) => d.label))];
      this.mapDepartmentColors(departments);

      premiumByDept.forEach(p => p.color = this.mappedDepartmentColors.get(p.label));
      this.premiumByDepartmentData = premiumByDept;
    },
    async loadPremiumByIndustry() {
      this.premiumByIndustryData = await managementAnalyticsService.premiumByIndustry(
          DateTime.fromJSDate(this.startDate).toISODate(),
          DateTime.fromJSDate(this.endDate).toISODate(),
          10,
          this.selectedDepartments,
          this.selectedSicIndustries,
          this.selectedIndustries,
          this.includeChildren,
          this.selectedCoverageTypes,
          this.selectedAgencyTypes,
          this.selectedProfitCenters,
      );
    },
    async loadPremiumByProduct() {
      this.premiumByProductData = await managementAnalyticsService.premiumByProduct(
          this.selectedSicIndustries,
          this.selectedIndustries,
          this.includeChildren,
          this.selectedAgencyTypes,
          this.selectedProfitCenters,
      );

      // assign colors to departments
      const departments = [...new Set(this.premiumByProductData.map((d) => d.label))];
      this.mapDepartmentColors(departments);
    },
    async loadDepartments() {
      this.departments = await referenceService.departments();
    },
    async loadSICIndustries() {
      this.sicIndustries = (await referenceService.sicCodes())
          .map(x => ({ label: x.categoryName, value: x.categoryCode }))
          .sort((a, b) => a.label.localeCompare(b.label))
          .filter((x, i, a) => a.findIndex(y => y.label === x.label) === i);
    },
    async loadIndustries() {
      this.industries = (await referenceService.industries())
        .map(x => ({ label: x?.name, value: x?.ID }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    async loadCoverageTypes() {
      this.coverageTypes = (await referenceService.coverageTypes()).map(x => ({ label: x.label, value: x.label }));
    },
    async loadAgencyTypes() {
      this.agencyTypes = (await referenceService.agencyTypes()).map((a, i) => ({ label: a.agencyName, value: a.agencyCode, color: defaultChartColors?.[i] ?? ''}));
    },
    async loadProfitCenters() {
      this.profitCenters = (await referenceService.profitCenters()).map((pc) => ({ label: pc.name, value: pc.id }));
    },
    clearAllFilters() {
      this.selectedDepartments = [];
      this.selectedCoverageTypes = [];
      this.selectedSicIndustries = [];
      this.selectedIndustries = [];
      this.selectedAgencyTypes = [];
      this.selectedProfitCenters = [];
      this.includeChildren = false;
    }
  },
  watch: {
    selectedDepartments() {
      this.refreshData();
    },
    selectedCoverageTypes() {
      this.refreshData();
    },
    selectedSicIndustries() {
      this.refreshData();
    },
    selectedIndustries() {
      this.refreshData();
    },
    selectedAgencyTypes() {
      this.refreshData();
    },
    selectedProfitCenters() {
      this.refreshData();
    },
    includeChildren() {
      this.refreshData();
    },
    showCompare() {
      this.loadPremiumByMonth();
    }
  },
  async mounted() {
    try {
      await Promise.all([
        this.loadAgencyTypes(),
        this.loadIndustries(),
        this.loadSICIndustries(),
        this.loadCoverageTypes(),
        this.loadDepartments(),
        this.loadProfitCenters(),
      ]);
    } catch (e) {
      console.error(e);
      this.error = FailedToLoadPage;
    }
  }
};
</script>

<style scoped>
.page-header {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  padding-bottom: 0.5rem;
}

.auto-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  align-items: start;
  grid-gap: 0.5em;
  margin-top: 1rem;
}

.doughnuts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  align-items: start;
}
</style>
